import React, { useRef, memo, useEffect, useCallback, useState} from 'react';
import Documented from 'components/help/Documented.js';
import {toggleStep} from 'operations/actions.js';
import {isStepSelected, isStepSelection} from './selection.js';
import {getStepClassAndStyle} from './getStepClassName.js';
import {connect} from 'react-redux';
import {getBarLength} from 'utils/getBarLength';
import StepData from 'pattern/StepData.js';
import {setStepToCopy} from 'actions/index.js';
import {setSingleStepToDrag} from 'actions/index.js';
import {setStepNumberDraggedTo} from 'actions/index.js';
import ConditionedComponent from 'components/ConditionedComponent.js';
import Notes from 'Notes';
import StepParameters from 'StepParameters';
import Colors from 'Colors.js';
import Track from 'track/Track.js';
import Resolution from 'engine/Resolution.js';
import {
    moveSteps,
    updateStepParameter,
       } from 'operations/actions.js';
import {mouseDown} from 'actions/index.js';
import {dragToggle} from 'actions/index.js';
import {setIsDraggingDuration} from 'actions/index.js';
import {setSelectedPianoRollSteps} from 'actions/index.js';
import {setStepDragging} from 'actions/index.js';
import {setCommandDown} from 'actions/index.js';
import {setStepsSelection} from 'actions/index.js';
import {setStepNumberDraggingFrom} from 'actions/index.js';
import {
    setContextualHelp,
    setCirklonParameterSelected
} from 'actions/index.js';
import {STEP} from 'constants/contextual-help.js';
import StepBottomComponent from 'components/StepBottomComponent.js';
import '../../Steps.scss';

const colors = new Colors();
const defaultStepColor='#92f4f6';
const defaultFillColor='#428282';
const defaultAlternateStepColor='#42f4ad';
const defaultAlternateFillColor='#442a82'; 
const defaultStepColorCursor='#ff9ed5';
const defaultAlternateStepColorCursor='#ff9ed5';
const cursorAndCurrent = '#ff5171';

const DURATION_RESOLUTION = new Resolution(16, false);
const DEFAULT_STEP_DATA = new StepData();

window.pianoRoll = 7;

const StepComponent = memo((props) => { 
    const color = colors.getColorForTrack(props.track);
    const ref = useRef(undefined);
    let state = props.sequencerEngine.store.getState(); 
    let stepToId = state.tracks.trackStepIds[props.track.id];
    let stepData = stepToId && stepToId[props.stepNumber];
    if (stepData === undefined || !stepData.on) {
        stepData = DEFAULT_STEP_DATA;
    }

    const isSelected = isStepSelected(props, stepData);
    const isSelection = isStepSelection(props);

    let {className, style, containerClassName} = getStepClassAndStyle(props, isSelected, isSelection, color, stepData);

    const onMouseDown = useCallback(() => {
       if (props.showParameters) {
           return;
       }
       props.mouseDown(true);
       props.sequencerEngine.select(props.trackNumber, props.stepNumber);

        if (isSelected) {
            // start dragging
            props.setStepNumberDraggingFrom(props.stepNumber);
        } else if (!isCommandDown(props) && props.on) {
            // setting single step to drag to be this!
            props.setSelectedSteps(undefined);

            let stepDatas = props.sequencerEngine.state.tracks.trackStepIds[props.track.id] || {};
            props.setSingleStepToDrag(stepDatas[props.stepNumber]);
            props.setStepNumberDraggingFrom(props.stepNumber);
        } else {
            props.setSelectedSteps(undefined);
            props.setStepsSelection(props.trackNumber, props.stepNumber, props.stepNumber);
        }
        props.setStepNumberDraggingFrom(props.stepNumber);
   }, [isSelected, props.showParameters, stepData, props.on, props.stepNumber, props.trackNumber, props.track]);

    const onMouseUp = useCallback(() => {
        props.setSingleStepToDrag(undefined);
        props.setIsDraggingDuration(false);
        props.setStepToCopy(undefined);

        let state = props.sequencerEngine.store.getState();
        if (!isCommandDown(props) && state.steps.stepNumberDraggedTo === props.stepNumber) {
            props.setStepNumberDraggedTo(undefined);
            return;
        }

        if (state.steps.stepNumberDraggingFrom !== props.stepNumber) {
            props.setStepNumberDraggedTo(undefined);
            return;
        }

        if (isCommandDown(props)) {
            props.setStepsSelection(undefined);
            props.sequencerEngine.cursor.show = true;
        
        } else {
            props.sequencerEngine.operationManager.dispatch(
                toggleStep(props.sequencerEngine.tracks[props.trackNumber], props.stepNumber, props.resolution));
            props.setSelectedSteps(undefined);
            props.sequencerEngine.select(props.trackNumber, props.stepNumber);
            props.setStepsSelection(undefined);
            props.sequencerEngine.cursor.track = props.trackNumber;
        }
        props.setStepNumberDraggedTo(undefined);

    }, [props.stepNumber, props.resolution, props.track, props.trackNumber]);

    const onMouseOver = useCallback((e) => {
        e.stopPropagation();
        
        let state = props.sequencerEngine.store.getState();
        if (state.keyboard.isDraggingDuration && state.keyboard.isMouseDown) {
            updateDuration();
            return;
        }

        if (state.steps.stepNumberDraggingFrom !== undefined && 
            (state.keyboard.selectedPianoRollSteps !== undefined || state.steps.singleStepToDrag)) {
            let transformation = getTransformation(state.steps.stepNumberDraggingFrom);
            let steps = state.keyboard.selectedPianoRollSteps !== undefined ?
                state.keyboard.selectedPianoRollSteps: [state.steps.singleStepToDrag];
            props.sequencerEngine.operationManager.dispatch(moveSteps(
                props.sequencerEngine.tracks[props.trackNumber],
                steps,
                transformation,
            props.resolution));

            props.setStepNumberDraggingFrom(props.stepNumber);
            props.setStepNumberDraggedTo(props.stepNumber);
            props.sequencerEngine.select(props.trackNumber, props.stepNumber);
        } else if (props.stepsSelection !== undefined) {
            // add to the selection
            let selection = props.stepsSelection;
            if (props.stepNumber >= selection.fromStep) {
                props.setStepsSelection(props.trackNumber, selection.fromStep, props.stepNumber);
            } else {
                props.setStepsSelection(props.trackNumber, props.stepNumber, selection.toStep);
            }
        }
    }, [props.stepsSelection,
        props.selectedSteps,
        props.track,
        props.trackNumber,
        props.resolution,
        props.stepNumber]);

    const updateDuration = useCallback(() => {
        let state = props.sequencerEngine.state;
        let stepDifference = 1 + props.stepNumber - state.keyboard.stepDragging;
        let duration = DURATION_RESOLUTION.transformStepAtResolution2(
            stepDifference, props.resolution);
        let stepDataToChange = state.keyboard.stepToCopy;
        let originalDuration = stepDataToChange.duration === null ?
            props.track.trackData.duration : stepDataToChange.duration;
        let durationMultiplier = duration / originalDuration;
        if (props.selectedSteps) {
            props.selectedSteps.forEach(
                step => updateStepDuration(step, duration));
        } else {
            updateStepDuration(stepDataToChange, duration);
        }
    }, [props.selectedSteps, props.track, props.resolution]);

    const updateStepDuration = (step, duration) => {
        let originalDuration = step.duration === null ?
            props.track.trackData.duration : step.duration;
        let durationMultiplier = duration / originalDuration;
        props.sequencerEngine.operationManager.dispatch(
            updateStepParameter(
                props.sequencerEngine.tracks[props.trackNumber],
                step,
                step.durationParameter,
                Math.max(0.1, duration)));
    };

    const getTransformation = useCallback((stepNumberDraggingFrom) => {
        return {
            stepDifference: props.stepNumber - stepNumberDraggingFrom,
            transposeDifference: 0
        };
    }, [props.stepNumber]);

    const dragStepDuration = useCallback((e) => {
        e.stopPropagation();
        let stepToId = props.sequencerEngine.state.tracks.trackStepIds[props.track.id];
        let stepData = stepToId && stepToId[props.stepNumber];
        if (!isSelected) {
            props.setSelectedSteps([stepData]);
        }
        props.setIsDraggingDuration(true);
        props.setStepDragging(props.stepNumber);
        props.setStepToCopy(stepData);
        props.mouseDown(true);
    }, [isSelected, stepData, props.track]);

    const isTriplet =  stepData &&
          ((stepData.position > 0.6 && stepData.position < 0.7)  ||
           (stepData.position >  0.3 && stepData.position < 0.4));

    let reverse = stepData && stepData.reverse;
    return (
        <Documented element={STEP}>
          <div
            onMouseOver={onMouseOver}
            className={containerClassName}>
          <div
            ref={ref}
            id={"stepdata-" + stepData.id}
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
            className={className} style={style}>
            {props.showParameters ?
                  <StepParameters
                        sequencerEngine={props.sequencerEngine}
                        track={props.track}
                        isMidi={props.isMidi}
                        onUpdateStepData={(stepData) => {
                        }}
                        onUpdateStepDuration={props.onUpdateStepDuration}
                        slices={props.slices}
                        stepData={stepData}>
                      </StepParameters> :
               ''}
               <ConditionedComponent condition={props.on}>
                 {reverse ? <i className="reverse-indicator"></i> : ''}
                 {stepData.group ? <div className="group-indicator"/> : ''}
                 {stepData.pLockedParameters.length > 0 ?
                  <div
                  style={{
                      backgroundColor: colors.getColorForHash(
                          getPLockKey(stepData))
                  }}
                    className="p-lock-indicator"/> : ''}
                 {isTriplet ? <div className="triplet">t</div> : ''}
                 <div
                    draggable={false}
                    onMouseDown={dragStepDuration}
                   className="right-edge"></div>
               </ConditionedComponent>
          </div>
          <StepBottomComponent
            stepData={stepData}
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
            sampleId={props.id}
            on={props.on}
            fill={props.fill}
            track={props.track}
            isCurrentStep={props.isCurrentStep}
            isCursor={props.isCursor}
          />
        </div>
        </Documented>
    );
});

const mapDispatchToProps = dispatch => {
    return {
        mouseDown: m => dispatch(mouseDown(m)),
        dragToggle: m => dispatch(dragToggle(m)),
        setContextualHelp: element => dispatch(setContextualHelp(element)),
        setStepToCopy: step  => dispatch(setStepToCopy(step)),
        setStepDragging: step  => dispatch(setStepDragging(step)),
        setSingleStepToDrag: step  => dispatch(setSingleStepToDrag(step)),
        setStepsSelection: (trackNumber, fromStep, toStep) => dispatch(setStepsSelection(trackNumber, fromStep, toStep)),
        setStepNumberDraggingFrom: (stepNumber) => dispatch(setStepNumberDraggingFrom(stepNumber)),
        setStepNumberDraggedTo: (stepNumber) => dispatch(setStepNumberDraggedTo(stepNumber)),
        setIsDraggingDuration: isDraggingDuration => dispatch(setIsDraggingDuration(isDraggingDuration)),
        setSelectedSteps: (steps) =>
            dispatch(setSelectedPianoRollSteps(steps)),
    }
};

const isCommandDown = (props) => {
    return props.sequencerEngine.store.getState().keyboard.isCommandDown;
};

function isScrolledIntoView(el, scrollArea) {
    // el = getParentView(el, 8);
    var rect = el.getBoundingClientRect();
    var elemTop = el.offsetTop;

    let height = scrollArea ? scrollArea.clientHeight : window.innerHeight;
    let scrollTop = scrollArea ? scrollArea.scrollTop : window.scrollY + 40;

    let isVisible = (elemTop - scrollTop >= 0) && (elemTop - scrollTop <= height + 10 );
    return isVisible;
}

function  getParentView(e, n=1) {
    let parentView = e;
    for (let i=0; i < n; i++) {
        parentView = parentView.parentElement;
    }
    return parentView;
}


const getPLockKey = (stepData) => {
    let sum = stepData.pLockedParameters.map(
        p => p.stepToValue[stepData.id])
        .reduce((a,b) => a+b, 0);
    if (sum < 31) {
        sum = Math.pow(2,sum);
    }
    return sum + '-' + stepData.pLockedParameters[0].id;
};

export default connect(null, mapDispatchToProps)(StepComponent);
